import { defineTransform } from '@client/core/data/concerns/parses-from-json/transform-map';
import { Nullable } from '@client/core/data/types';
import { parseISO } from '@client/core/utils/date';
import { metersToMiles } from '@client/core/utils/meters-to-miles';
import { CompStatus } from '@client/features/comps/types';
import { buildFullAddress } from '@client/core/address/build-full-address';
import { Address } from '@client/core/address/types';

/**
 * @see app/Http/Resources/CompResource.php
 */
export class Comp {
  constructor(
    public readonly id: string,
    public readonly projectId: number,
    /**
     * @deprecated This feature has been removed
     */
    public readonly orderNumber: number,
    public readonly address1: string,
    public readonly address2: Nullable<string>,
    public readonly city: Nullable<string>,
    public readonly region: Nullable<string>,
    public readonly postalCode: Nullable<string>,
    public readonly country: Nullable<string>,
    // Distance in Miles. But this is stored on backend in Meters. See JSON transform below.
    public readonly distanceToProject: Nullable<number>,
    public readonly latitude: number,
    public readonly longitude: number,
    public readonly neighborhood: Nullable<string>,
    public readonly propertyType: Nullable<string>,
    public readonly yearBuilt: Nullable<number>,
    public readonly beds: number,
    public readonly fullBaths: number,
    public readonly halfBaths: number,
    public readonly squareFeet: number,
    public readonly parkingSpaces: Nullable<number>,
    public readonly purchasePrice: number,
    public readonly soldDate: Nullable<Date>,
    public readonly isSelected: boolean,
    public readonly isManuallyEntered: boolean,
    public readonly status: Nullable<CompStatus>,
    public readonly daysOnMarket: Nullable<number>,
    public readonly notes: Nullable<string>,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  get fullAddress(): string {
    const address: Address = {
      address1: this.address1,
      address2: this.address2,
      city: this.city,
      postalCode: this.postalCode,
      region: this.region,
    };

    return buildFullAddress(address);
  }

  /**
   * If the purchase price is known, return an abbreviated text version, such
   * as "245K".
   */
  getShortPurchasePrice(): string | null {
    if (!this.purchasePrice) {
      return null;
    }

    // Small purchase price
    if (this.purchasePrice < 1_000) {
      return this.purchasePrice.toString();
    }

    // Millions like 45.6M
    if (this.purchasePrice >= 1_000_000) {
      const millions = this.purchasePrice / 1_000_000;
      const formattedMillions = Number.isInteger(millions) ? millions.toFixed(0) : millions.toFixed(1);

      return `${formattedMillions}M`;
    }

    // 1K to 999K
    const thousands = Math.floor(this.purchasePrice / 1_000);

    return `${thousands}K`;
  }
}

defineTransform(Comp, (json, { fromSnakeCaseKeys, withDefault, nullOr }) => {
  return {
    ...fromSnakeCaseKeys(),
    soldDate: nullOr(json.sold_date, () => parseISO(json.sold_date)),
    address1: json.address_1,
    address2: json.address_2,
    beds: withDefault(json.beds, () => json.beds, 0),
    fullBaths: withDefault(json.full_baths, () => json.full_baths, 0),
    halfBaths: withDefault(json.half_baths, () => json.half_baths, 0),
    squareFeet: withDefault(json.square_feet, () => json.square_feet, 0),
    purchasePrice: withDefault(json.purchase_price, () => json.purchase_price, 0),
    createdAt: parseISO(json.created_at),
    updatedAt: parseISO(json.updated_at),
    distanceToProject: nullOr(json.distance_to_project, () => metersToMiles(json.distance_to_project)),
  };
});
